
/**
 * Color Card Navigation - Horizontal scroll controls with drag support
 * Handles navigation buttons and mouse drag for the color cards container
 */

class ColorCardNavigation {
  constructor() {
    this.container = null;
    this.leftButton = null;
    this.rightButton = null;
    this.wrapper = null;
    this.scrollAmount = 240; // Match gallery scroll amount (was 324)
    this.dragHandler = null;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.container = document.querySelector('[data-color-cards-container]');
    this.leftButton = document.querySelector('[data-nav-left]');
    this.rightButton = document.querySelector('[data-nav-right]');
    this.wrapper = document.querySelector('[data-navigation-wrapper]');

    if (!this.container || !this.leftButton || !this.rightButton) {
      console.log('ColorCardNavigation: Required elements not found');
      return;
    }

    this.bindEvents();
    this.setupDragScrolling();
    this.updateButtonVisibility();
    
    console.log('ColorCardNavigation: Initialized successfully with drag support');
  }

  setupDragScrolling() {
    // Initialize drag scroll handler with gallery-matching settings
    if (window.DragScrollHandler && this.container) {
      this.dragHandler = new window.DragScrollHandler(this.container, {
        scrollAmount: 1.5, // Match gallery sensitivity (was 1.2)
        enableVertical: false // Match gallery settings
      });
    }
  }

  bindEvents() {
    // Navigation button clicks
    this.leftButton.addEventListener('click', () => this.scrollLeft());
    this.rightButton.addEventListener('click', () => this.scrollRight());

    // Container scroll events to update button states
    this.container.addEventListener('scroll', () => this.updateButtonVisibility());

    // Observe container content changes
    const observer = new MutationObserver(() => {
      // Delay to allow CSS to be applied
      setTimeout(() => this.updateButtonVisibility(), 100);
    });
    
    observer.observe(this.container, {
      childList: true,
      subtree: true
    });

    // Window resize
    window.addEventListener('resize', () => {
      setTimeout(() => this.updateButtonVisibility(), 100);
    });
  }

  scrollLeft() {
    if (!this.container) return;
    
    this.container.scrollBy({
      left: -this.scrollAmount,
      behavior: 'smooth'
    });
  }

  scrollRight() {
    if (!this.container) return;
    
    this.container.scrollBy({
      left: this.scrollAmount,
      behavior: 'smooth'
    });
  }

  updateButtonVisibility() {
    if (!this.container || !this.leftButton || !this.rightButton) return;

    const containerRect = this.container.getBoundingClientRect();
    const hasOverflow = this.container.scrollWidth > this.container.clientWidth;
    const scrollLeft = this.container.scrollLeft;
    const maxScrollLeft = this.container.scrollWidth - this.container.clientWidth;

    // Show/hide buttons based on overflow
    if (hasOverflow) {
      // Show left button if not at the beginning
      if (scrollLeft > 5) { // Small threshold to account for rounding
        this.leftButton.classList.remove('hidden');
      } else {
        this.leftButton.classList.add('hidden');
      }

      // Show right button if not at the end
      if (scrollLeft < maxScrollLeft - 5) { // Small threshold to account for rounding
        this.rightButton.classList.remove('hidden');
      } else {
        this.rightButton.classList.add('hidden');
      }
    } else {
      // No overflow, hide both buttons
      this.leftButton.classList.add('hidden');
      this.rightButton.classList.add('hidden');
    }

    console.log('ColorCardNavigation: Button visibility updated', {
      hasOverflow,
      scrollLeft,
      maxScrollLeft,
      leftVisible: !this.leftButton.classList.contains('hidden'),
      rightVisible: !this.rightButton.classList.contains('hidden')
    });
  }

  // Public method to trigger visibility update (can be called from other modules)
  refresh() {
    setTimeout(() => this.updateButtonVisibility(), 50);
  }

  // Cleanup method
  destroy() {
    if (this.dragHandler) {
      this.dragHandler.destroy();
      this.dragHandler = null;
    }
  }
}

// Initialize navigation
const colorCardNavigation = new ColorCardNavigation();

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.colorCardNavigation = colorCardNavigation;
}
